import { arrayOf, bool, func, shape, string } from "prop-types";
import React, { Component } from "react";
import Row from "react-bootstrap/lib/Row";
import { Redirect } from "react-router-dom";

import * as configUtils from "util/config";
import * as i18nUtils from "util/i18n";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import { actions as layoutActions } from "reducers/layout";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getPostLoginHomePath } from "util/v3UxConfig";

import Circle from "pages/_components/Circle";
import Container from "pages/_components/Container";
import FormattedAmount from "pages/_components/FormattedAmount";
import Head from "pages/_components/Head";
import Heading from "pages/_components/Heading";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";

import { resizableRoute } from "pages/_components/Resizable";
import { getCapType } from "../../../util/general";

class SignatureSchemeDetail extends Component {
    static propTypes = {
        actions: shape({
            modifySignaturesSchemePreRequest: func,
        }),
        activeEnvironment: shape({
            administrationScheme: string,
        }).isRequired,
        fetching: bool,
        isDesktop: bool.isRequired,
        match: shape({
            params: shape({
                idSignature: string,
            }),
        }),
        routerActions: shape({
            goBack: func,
            push: func,
        }),
        signature: shape({
            signatureType: string,
            signatureDispatch: bool,
        }),
        environmentProducts: arrayOf(
            shape({
                idProduct: string,
            }),
        ).isRequired,
        embeddedInV3Shell: bool,
        dispatch: func.isRequired,
    };

    static defaultProps = {
        actions: null,
        fetching: false,
        match: null,
        routerActions: null,
        signature: null,
        embeddedInV3Shell: false,
    };

    componentDidMount() {
        const {
            actions,
            dispatch,
            embeddedInV3Shell,
            match: {
                params: { idSignature },
            },
        } = this.props;

        actions.modifySignaturesSchemePreRequest(idSignature);
        if (embeddedInV3Shell && isUxDashboardImprovementsEnabled()) {
            dispatchV3ShellLayout(dispatch, {
                titleKey: "administration.signature.scheme.detail",
                goBackPath: getPostLoginHomePath(true),
                rightContentMobile: null,
                scopes: ["signatureSchemeDetail"],
            });
        }
    }

    componentWillUnmount() {
        const { dispatch, embeddedInV3Shell } = this.props;

        if (embeddedInV3Shell && isUxDashboardImprovementsEnabled()) {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        }
    }

    handleBack = () => {
        const {
            activeEnvironment: { administrationScheme },
            routerActions,
        } = this.props;

        routerActions.push(`/administration/${administrationScheme}/signaturesSchemes`);
    };

    handleModify = () => {
        const {
            activeEnvironment: { administrationScheme },
            signature,
            routerActions,
        } = this.props;

        routerActions.push(`/administration/${administrationScheme}/signaturesSchemes/${signature.idSignature}`);
    };

    handleDelete = () => {
        const {
            activeEnvironment: { administrationScheme },
            routerActions,
            signature,
        } = this.props;

        routerActions.push(`/administration/${administrationScheme}/signaturesSchemes/remove/${signature.idSignature}`);
    };

    renderPageHeader = () => {
        const {
            signature,
            activeEnvironment: { administrationScheme },
        } = this.props;
        const name = `${i18nUtils.get("administration.signatures.detail")}`;
        let letters = "";
        if (signature) {
            const { signatureGroup } = signature;
            letters += administrationScheme === "medium" ? `${`${signatureGroup.length}`}` : `${`${signatureGroup}`}`;
        }

        return (
            <>
                <div className="admin-detail-head admin-detail-head-section px-0">
                    <Head onBack={this.handleBack} />
                    <div className="mt-2 mb-4">
                        <Head
                            centerElement={() => (
                                <h1 className="title-content">
                                    {name}
                                    {letters !== "" && <span className="pl-2 letter-spacing-05">{letters}</span>}
                                </h1>
                            )}
                            toolbarItemClassName="pr-0"
                            handleDeleteClick={() => {
                                this.handleDelete();
                            }}
                            handleModifyClick={() => this.handleModify()}
                            hasInlineButtons
                        />
                    </div>
                </div>
            </>
        );
    };

    signatureToLabel = (signatureDispatch) => (
        <Circle
            smallSize
            value={
                signatureDispatch ? i18nUtils.get("global.yes").toUpperCase() : i18nUtils.get("global.no").toUpperCase()
            }
            bgColor={signatureDispatch ? "#0be0a2" : "#fc5f5f"}
        />
    );

    renderContent = () => {
        const { signature, environmentProducts, embeddedInV3Shell } = this.props;
        const showLegacyNotification = !embeddedInV3Shell || !isUxDashboardImprovementsEnabled();
        const signatureDispatchValue = signature && signature.signatureDispatch;
        let signatureType = "administration.signatures.detail.signatureType.";
        let functionalGroups = null;
        if (signature) {
            signatureType += `${signature.signatureType}`;
            const { signatureFeatures } = signature;
            functionalGroups = signatureFeatures.length > 0 ? signatureFeatures : null;
        }
        const signatureProducts =
            environmentProducts &&
            environmentProducts.filter(({ idProduct }) => signature && signature.products.includes(idProduct));
        const labelCapFrequency = "administration.signatures.transactions.capFrequency.";
        const labelFunctionalGroups = "administration.signatures.functionalGroups.";
        const typeCap = getCapType(functionalGroups);

        return (
            <>
                {showLegacyNotification && <Notification scopeToShow="signatureSchemeDetail" />}
                {this.renderPageHeader()}
                <MainContainer>
                    <div className="above-the-fold signature-detail">
                        <div className="m-3">
                            <Row className="content-data background-white">
                                <div className="ml-3 mt-3">
                                    <Heading.DataGroup
                                        containerClassName="transfer-data data-wrapper-flex"
                                        label="administration.signatures.list.liberating"
                                        labelClassName="mb-3"
                                        data={this.signatureToLabel(signatureDispatchValue)}
                                    />
                                </div>
                            </Row>
                        </div>
                        <Container className="flex-grow admin-detail" rowClassName="justify-content-start">
                            <Container.Column md={3} sm={12} className="pt-4">
                                <Heading.DataGroup
                                    containerClassName="transfer-data data-wrapper"
                                    label="administration.signatures.list.transactionType"
                                    labelClassName="mb-3"
                                    data={signature ? i18nUtils.get(signatureType) : "no hay datos"}
                                />
                            </Container.Column>

                            <Container.Column md={3} sm={12} className="pt-4">
                                <Heading.DataGroup
                                    containerClassName="transfer-data data-wrapper"
                                    label="administration.signatures.detail.signatureType.topAmount"
                                    labelClassName="mb-3"
                                    data={
                                        signature && signature.capList[0] ? (
                                            <FormattedAmount
                                                className="data-amount"
                                                frequencyInSpan
                                                currency={configUtils.get("core.masterCurrency")}
                                                quantity={
                                                    signature.capList[0].maximum === -1
                                                        ? configUtils.get(
                                                              `default_cap_${typeCap}_${signature.signatureType}`,
                                                          )
                                                        : signature.capList[0].maximum
                                                }
                                                frequency={i18nUtils.get(
                                                    "administration.signatures.create.advanced.topAmountPerPeriod",
                                                    null,
                                                    {
                                                        period: i18nUtils.get(
                                                            `${labelCapFrequency}${signature.capList[0].frequency}`,
                                                        ),
                                                    },
                                                )}
                                                notBold
                                            />
                                        ) : (
                                            <p> - </p>
                                        )
                                    }
                                />
                            </Container.Column>

                            <Container.Column md={3} sm={12} className="pt-4">
                                <Heading.DataGroup
                                    containerClassName="transfer-data data-wrapper"
                                    label="administration.permissions.functionalities"
                                    labelClassName="mb-3"
                                    data={
                                        functionalGroups !== null ? (
                                            <ul className="list">
                                                {functionalGroups.map((functionalGroup) => {
                                                    const baseKey = `${labelFunctionalGroups}${functionalGroup}`;
                                                    const i18nKey =
                                                        functionalGroup === "transfer.multiple.send"
                                                            ? getMultipleTransfersI18nKey(baseKey)
                                                            : baseKey;
                                                    return (
                                                        <li key={functionalGroup} className="list-item mb-2">
                                                            <div className="chip-signature-scheme">
                                                                <span className="data-desc">
                                                                    {i18nUtils.get(i18nKey)}
                                                                </span>
                                                            </div>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <p>-</p>
                                        )
                                    }
                                />
                            </Container.Column>

                            <Container.Column md={3} sm={12} className="pt-4">
                                <Heading.DataGroup
                                    containerClassName="transfer-data data-wrapper"
                                    label="administration.signatures.detail.signatureType.productList"
                                    labelClassName="mb-3"
                                    data={
                                        signatureProducts && signatureProducts.length ? (
                                            <ul className="list">
                                                {signatureProducts.map((product) => (
                                                    <li key={product.idProduct} className="list-item mb-2">
                                                        <div className="chip-signature-scheme">
                                                            <span className="data-desc">{product.label}</span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p> - </p>
                                        )
                                    }
                                />
                            </Container.Column>
                        </Container>
                    </div>
                </MainContainer>
            </>
        );
    };

    render() {
        const { fetching, isDesktop } = this.props;
        const isLoading = fetching;
        if (!isDesktop) {
            return <Redirect to="/desktop" />;
        }

        return <PageLoading loading={isLoading}>{!isLoading && this.renderContent()}</PageLoading>;
    }
}

export default resizableRoute(SignatureSchemeDetail);
